import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map } from 'rxjs';
import {
  signIn,
  signOut,
  signUp,
  confirmSignUp,
  autoSignIn,
  resetPassword,
  confirmResetPassword,
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
  type AuthUser,
} from 'aws-amplify/auth';
import { environment } from '../../../environments/environment';

interface UserProfile {
  givenName: string;
  familyName: string;
  email: string;
  subscriptionTier?: string;
}

const EMPTY_PROFILE: UserProfile = { givenName: '', familyName: '', email: '' };

/**
 * Where sign-in landed. Anything other than DONE means Cognito needs another
 * step before the user has a session, and the caller must route them to it.
 *   RESET_PASSWORD  — an admin reset the password; a code has been emailed.
 *   CONFIRM_SIGN_UP — the account was never confirmed.
 */
export type SignInOutcome = 'DONE' | 'RESET_PASSWORD' | 'CONFIRM_SIGN_UP';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly userSubject = new BehaviorSubject<AuthUser | null>(null);
  private readonly profileSubject = new BehaviorSubject<UserProfile>(EMPTY_PROFILE);

  readonly currentUser$: Observable<AuthUser | null> = this.userSubject.asObservable();
  readonly isAuthenticated$: Observable<boolean> = this.userSubject.pipe(
    map((user) => user !== null),
  );

  readonly displayName$: Observable<string> = this.profileSubject.pipe(
    map(p => {
      if (p.givenName && p.familyName) return `${p.givenName} ${p.familyName}`;
      if (p.givenName) return p.givenName;
      return p.email || 'User';
    }),
  );

  readonly profile$ = this.profileSubject.asObservable();

  readonly tierLabel$: Observable<string> = this.profileSubject.pipe(
    map(p => {
      const map: Record<string, string> = { FREE: 'Free', PRO: 'Pro', ENTERPRISE: 'Enterprise' };
      return map[p.subscriptionTier ?? ''] ?? 'Free';
    }),
  );

  readonly userInitials$: Observable<string> = this.profileSubject.pipe(
    map(p => {
      if (p.givenName && p.familyName) {
        return `${p.givenName[0]}${p.familyName[0]}`.toUpperCase();
      }
      if (p.givenName) return p.givenName.slice(0, 2).toUpperCase();
      return p.email.slice(0, 2).toUpperCase() || '--';
    }),
  );

  constructor() {
    this.refreshUser();
  }

  async signIn(username: string, password: string): Promise<SignInOutcome> {
    const { isSignedIn, nextStep } = await signIn({ username, password });
    if (isSignedIn) {
      this.userSubject.next(await getCurrentUser());
      await this.refreshProfile();
      return 'DONE';
    }
    switch (nextStep.signInStep) {
      case 'RESET_PASSWORD':  return 'RESET_PASSWORD';
      case 'CONFIRM_SIGN_UP': return 'CONFIRM_SIGN_UP';
      default:
        // e.g. TOTP MFA challenges — not supported in the UI yet. Fail loudly
        // rather than navigating on as if signed in.
        throw new Error('This sign-in step is not supported yet. Please contact support.');
    }
  }

  async signUp(email: string, password: string, givenName: string, familyName: string): Promise<void> {
    await signUp({
      username: email,
      password,
      options: {
        userAttributes: { given_name: givenName, family_name: familyName },
        autoSignIn: true,
      },
    });
  }

  async confirmSignUp(email: string, code: string): Promise<void> {
    await confirmSignUp({ username: email, confirmationCode: code });
    try {
      const { isSignedIn } = await autoSignIn();
      if (isSignedIn) {
        this.userSubject.next(await getCurrentUser());
        await this.refreshProfile();
      }
    } catch {
      // Auto sign-in is best-effort. The caller navigates to /select-plan;
      // authGuard handles the unauthenticated case by redirecting to /login.
    }
  }

  /**
   * Emails a password-reset code. The app client has preventUserExistenceErrors
   * on, so this resolves for unknown emails too — callers must not reveal
   * whether the account exists.
   */
  async requestPasswordReset(email: string): Promise<void> {
    await resetPassword({ username: email });
  }

  /** Sets a new password using the emailed code. Does not sign the user in. */
  async confirmPasswordReset(email: string, code: string, newPassword: string): Promise<void> {
    await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
  }

  async signOut(): Promise<void> {
    await signOut();
    this.userSubject.next(null);
    this.profileSubject.next(EMPTY_PROFILE);
  }

  async getIdToken(): Promise<string | null> {
    try {
      const { tokens } = await fetchAuthSession();
      return tokens?.idToken?.toString() ?? null;
    } catch {
      return null;
    }
  }

  private async refreshUser(): Promise<void> {
    try {
      this.userSubject.next(await getCurrentUser());
      await this.refreshProfile();
    } catch {
      this.userSubject.next(null);
      this.profileSubject.next(EMPTY_PROFILE);
    }
  }

  private async refreshProfile(): Promise<void> {
    try {
      const attrs = await fetchUserAttributes();
      const profile: UserProfile = {
        givenName: (attrs['given_name'] as string) ?? '',
        familyName: (attrs['family_name'] as string) ?? '',
        email: (attrs['email'] as string) ?? '',
      };

      if (profile.givenName || profile.familyName) {
        this.profileSubject.next(profile);
        return;
      }

      // Cognito attributes have no name — look up the DynamoDB record.
      // Uses fetch() directly to avoid a circular dependency with the HTTP interceptor.
      try {
        const user = await getCurrentUser();
        const token = await this.getIdToken();
        if (token && user.userId) {
          const resp = await fetch(`${environment.apiUrl}/api/v1/users/${user.userId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (resp.ok) {
            const data = await resp.json() as { givenName?: string; familyName?: string; subscriptionTier?: string };
            this.profileSubject.next({
              givenName: data.givenName ?? '',
              familyName: data.familyName ?? '',
              email: profile.email,
              subscriptionTier: data.subscriptionTier,
            });
            return;
          }
        }
      } catch { /* network unavailable — fall through to Cognito profile */ }

      this.profileSubject.next(profile);
    } catch {
      this.profileSubject.next(EMPTY_PROFILE);
    }
  }
}
